import type { MaybeRefOrGetter, Ref } from 'vue';
import type { FoldableBreadcrumbItem, RenderableBreadcrumbItem } from '#layers/ui/types/breadcrumb-foldable';
import { computed, readonly, ref, toValue } from 'vue';
import { useOverflowCore } from '#layers/ui/composables/use-overflow-core';
import { calculateBreadcrumbFold } from '#layers/ui/utils/calculate-breadcrumb-fold';

export const LAST_BREADCRUMB_ITEM_MIN_WIDTH_PX = 64;
export const BREADCRUMB_ELLIPSIS_SIZE_PX = 28;
export const BREADCRUMB_TRAILING_SPACE_PX = 24;

const ELLIPSIS_ITEM: RenderableBreadcrumbItem = { slot: 'ellipsis', ellipsis: true, folded: false, ui: { item: 'px-0' } };

/**
 * Largest hidden horizontal overflow anywhere in `el`'s subtree.
 * Which element clips varies by crumb type, so scan them all.
 */
function hiddenOverflow(el: HTMLElement): number {
	let max = 0;

	for (const node of [el, ...el.querySelectorAll<HTMLElement>('*')]) {
		max = Math.max(max, node.scrollWidth - node.clientWidth);
	}

	return max;
}

export interface UseBreadcrumbOverflowOptions {
	rootRef: MaybeRefOrGetter<HTMLElement | null>;
	listRef: MaybeRefOrGetter<HTMLElement | null>;
	items: MaybeRefOrGetter<FoldableBreadcrumbItem[]>;
}

export interface UseBreadcrumbOverflowReturn {
	renderItems: Readonly<Ref<RenderableBreadcrumbItem[]>>;
	foldedItems: Readonly<Ref<RenderableBreadcrumbItem[]>>;
	lastCrumbReleased: Readonly<Ref<boolean>>;
}

export function useBreadcrumbOverflow(options: UseBreadcrumbOverflowOptions): UseBreadcrumbOverflowReturn {
	const foldedCount = ref(0);
	const lastReleased = ref(false);
	const firstFolded = ref(false);
	const foldStart = computed(() => (firstFolded.value ? 0 : 1));
	const items = computed(() => toValue(options.items));
	const itemCount = computed(() => items.value.length);

	const foldedItems = computed<RenderableBreadcrumbItem[]>(() => {
		if (foldedCount.value === 0) return [];
		return items.value.slice(foldStart.value, foldStart.value + foldedCount.value);
	});

	// Keep every crumb in the array (folded ones flagged) in original order, with the … at the fold
	// boundary, so measurement reads widths in order.
	const renderItems = computed<RenderableBreadcrumbItem[]>(() => {
		if (foldedCount.value === 0) return items.value.map((item) => ({ ...item, folded: false }));

		const start = foldStart.value;
		const before = items.value.slice(0, start).map((item) => ({ ...item, folded: false }));
		const folded = items.value.slice(start, start + foldedCount.value).map((item) => ({ ...item, folded: true }));
		const after = items.value.slice(start + foldedCount.value).map((item) => ({ ...item, folded: false }));
		return [...before, ELLIPSIS_ITEM, ...folded, ...after];
	});

	useOverflowCore({
		containerRef: options.rootRef,
		itemsRef: options.listRef,
		itemCount,
		observeFonts: true,
		recalculate,
	});

	function recalculate() {
		const rootEl = toValue(options.rootRef);
		const listEl = toValue(options.listRef);

		if (!rootEl || !listEl || itemCount.value === 0) {
			foldedCount.value = 0;
			lastReleased.value = false;
			firstFolded.value = false;
			return;
		}

		// Couples to DBreadcrumb's `data-slot` DOM; re-check on any @nuxt/ui bump (e2e guard: data-model/breadcrumbs.spec.ts).
		const list = [...listEl.querySelectorAll<HTMLElement>(':scope > li[data-slot="item"]')];
		if (list.length !== renderItems.value.length) return;

		const crumbItems = renderItems.value
			.map((item, index) => ({ item, li: list[index]! }))
			.filter((entry) => !entry.item.ellipsis)
			.map((entry) => entry.li);

		if (crumbItems.length === 0) return;

		// Folded crumbs stay rendered (hidden), so offsetWidth is readable. The last crumb only releases, never folds, so it's excluded here and carried as its natural width below.
		const foldableItemWidths = crumbItems.slice(0, -1).map((li) => li.offsetWidth);
		const lastCrumbItem = crumbItems.at(-1)!;

		// Last crumb's natural width, not its shrunk render width
		const lastNaturalWidth = lastCrumbItem.offsetWidth + hiddenOverflow(lastCrumbItem);

		const separator = listEl.querySelector<HTMLElement>(':scope > li[data-slot="separator"]');
		const gap = separator?.offsetWidth ?? 0;
		const containerWidth = Math.max(0, rootEl.clientWidth - BREADCRUMB_TRAILING_SPACE_PX);

		const fold = calculateBreadcrumbFold({
			foldableItemWidths,
			lastNaturalWidth,
			containerWidth,
			ellipsisWidth: BREADCRUMB_ELLIPSIS_SIZE_PX,
			lastMinWidth: LAST_BREADCRUMB_ITEM_MIN_WIDTH_PX,
			gap,
		});

		foldedCount.value = fold.foldedCount;
		lastReleased.value = fold.lastReleased;
		firstFolded.value = fold.firstFolded;
	}

	return {
		renderItems,
		foldedItems,
		lastCrumbReleased: readonly(lastReleased),
	};
}
